import { apiClient } from './apiClient';

/**
 * Claim service for API operations
 */
export class ClaimService {
  constructor() {
    this.BASE_URL = '/claims';
  }

  /**
   * Create a new claim
   * @param {Object} claimData - Claim data to be created
   * @returns {Promise<Object>} Created claim
   */
  async createClaim(claimData) {
    try {
      const response = await apiClient.post(this.BASE_URL, claimData);
      return response;
    } catch (error) {
      console.error("Error creating claim. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Unified search/query claims with support for filters, vector search, and pagination
   * This replaces getClaims, advancedSearch, and legacy searchClaimsByTerm methods
   * 
   * @param {Object} params - Search parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @param {string} params.sortBy - Field to sort by (default: 'updatedAt')
   * @param {string} params.sortOrder - Sort order 'asc' or 'desc' (default: 'desc')
   * @param {string} params.queryText - Natural language query for vector search (optional)
   * @param {number} params.vectorTopK - Number of vector results (default: 100)
   * @param {number} params.minSimilarity - Minimum similarity score 0-1 (default: 0)
   * @param {string|string[]} params.status - Filter by status
   * @param {string} params.patientId - Filter by patient ID
   * @param {string} params.patientName - Filter by patient name
   * @param {string} params.providerId - Filter by provider ID
   * @param {string} params.providerName - Filter by provider name
   * @param {string} params.caseType - Filter by case type (MVA, PI, WC)
   * @param {string} params.claimId - Filter by claim ID
   * @param {string|Date} params.dateOfServiceStart - Filter by DOS start
   * @param {string|Date} params.dateOfServiceEnd - Filter by DOS end
   * @param {string|Date} params.dateSubmittedStart - Filter by submitted date start
   * @param {string|Date} params.dateSubmittedEnd - Filter by submitted date end
   * @param {string} params.search - Legacy prefix search (backward compatibility)
   * @param {string} params.role - User role for filtering
   * 
   * @returns {Promise<Object>} Unified response with data, pagination, and metadata
   */
  async searchClaims(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Pagination
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());

      // Cursor-based pagination (for efficient Firestore queries)
      if (params.cursor) queryParams.append('cursor', params.cursor);
      if (params.cursorDirection) queryParams.append('cursorDirection', params.cursorDirection);

      // Sorting
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      // Vector search
      if (params.queryText) queryParams.append('queryText', params.queryText);
      if (params.vectorTopK) queryParams.append('vectorTopK', params.vectorTopK.toString());
      if (params.minSimilarity !== undefined) queryParams.append('minSimilarity', params.minSimilarity.toString());

      // Traditional filters
      if (params.status) {
        if (Array.isArray(params.status)) {
          params.status.forEach(s => queryParams.append('status', s));
        } else {
          queryParams.append('status', params.status);
        }
      }
      // Has Appeals filter (boolean)
      if (params.hasAppeals !== undefined && params.hasAppeals !== null) {
        queryParams.append('hasAppeals', String(params.hasAppeals));
      }
      if (params.hasPrescription !== undefined && params.hasPrescription !== null) {
        queryParams.append('hasPrescription', String(params.hasPrescription));
      }
      if (params.isTransfered !== undefined && params.isTransfered !== null) {
        queryParams.append('isTransfered', String(params.isTransfered));
      }
      if (params.isRebill !== undefined && params.isRebill !== null) {
        queryParams.append('isRebill', String(params.isRebill));
      }
      // Has Litigation filter (boolean)
      if (params.isLitigated !== undefined && params.isLitigated !== null) {
        queryParams.append('isLitigated', String(params.isLitigated));
      }
      if (params.isOverallVerified !== undefined && params.isOverallVerified !== null) {
        queryParams.append('isOverallVerified', String(params.isOverallVerified));
      }
      if (params.isMissingInfo !== undefined && params.isMissingInfo !== null) {
        queryParams.append('isMissingInfo', String(params.isMissingInfo));
      }
      if (params.patientId) queryParams.append('patientId', params.patientId);
      if (params.patientIds) {
        if (Array.isArray(params.patientIds)) {
          params.patientIds.forEach((id) => {
            if (id) queryParams.append('patientIds', id);
          });
        } else {
          queryParams.append('patientIds', params.patientIds);
        }
      }
      // if (params.patientName) queryParams.append('patientName', params.patientName);
      if (params.firstName) queryParams.append('firstName', params.firstName);
      if (params.lastName) queryParams.append('lastName', params.lastName);
      if (params.fullRxNumber) queryParams.append('fullRxNumber', params.fullRxNumber);
      if (params.providerId) queryParams.append('providerId', params.providerId);
      if (params.providerName) queryParams.append('providerName', params.providerName);
      if (params.caseType) queryParams.append('caseType', params.caseType);
      if (params.caseNumber) queryParams.append('caseNumber', params.caseNumber);
      if (params.claimId) queryParams.append('claimId', params.claimId);
      if (params.dateOfBirth) queryParams.append('dateOfBirth', params.dateOfBirth.toString());
      // Date filters
      if (params.dateOfServiceStart) queryParams.append('dateOfServiceStart', params.dateOfServiceStart.toString());
      if (params.dateOfServiceEnd) queryParams.append('dateOfServiceEnd', params.dateOfServiceEnd.toString());
      if (params.createdAtStart) queryParams.append('createdAtStart', params.createdAtStart.toString());
      if (params.createdAtEnd) queryParams.append('createdAtEnd', params.createdAtEnd.toString());
      if (params.updatedAtStart) queryParams.append('updatedAtStart', params.updatedAtStart.toString());
      if (params.updatedAtEnd) queryParams.append('updatedAtEnd', params.updatedAtEnd.toString());
      if (params.dateSubmittedStart) queryParams.append('dateSubmittedStart', params.dateSubmittedStart.toString());
      if (params.dateSubmittedEnd) queryParams.append('dateSubmittedEnd', params.dateSubmittedEnd.toString());

      // Role
      if (params.role) queryParams.append('role', params.role);

      // Legacy search
      if (params.search) queryParams.append('search', params.search);

      const url = `${this.BASE_URL}?${queryParams.toString()}`;
      const response = await apiClient.get(url);

      // Always return the full response structure
      return response;
    } catch (error) {
      console.error("Error searching claims. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Update an existing claim
   * @param {string} id - Claim ID
   * @param {Object} claim - Updated claim data
   * @returns {Promise<Object>} Updated claim
   */
  async updateClaim(id, claim) {
    return apiClient.patch(`${this.BASE_URL}/${id}`, claim);
  }

  async getClaimById(id) {
    return apiClient.get(`${this.BASE_URL}/${id}`)
  }
  /**
   * Update billing details for medications in a claim
   * @param {string} claimId - Claim ID
   * @param {Array} medicationUpdates - Array of billing updates per medication
   * @param {Object} paymentDetails - Optional payment details
   * @param {string} status - Optional status to set on the claim
   * @returns {Promise<Object>} Updated claim
   */
  async updateClaimMedications(claimId, medicationUpdates, paymentDetails, status) {
    try {
      const response = await apiClient.patch(`${this.BASE_URL}/${claimId}/medications/`, {
        medicationUpdates,
        paymentDetails,
        status
      });
      return response;
    } catch (error) {
      console.error("Error updating claim medications billing. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Get a single claim by ID
   * @param {string} id - Claim ID
   * @returns {Promise<Object>} Claim data
   */
  async getClaim(id) {
    try {
      const response = await apiClient.get(`${this.BASE_URL}/${id}`);
      return response;
    } catch (error) {
      console.error("Error fetching claim by ID:", error);
      throw error;
    }
  }

  async getLopReadiness(id) {
    try {
      const response = await apiClient.get(`${this.BASE_URL}/${id}/lop-readiness`);
      return response;
    } catch (error) {
      console.error("Error fetching LOP readiness:", error);
      throw error;
    }
  }

  async createLopBatch(id) {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/${id}/create-lop-batch`);
      return response;
    } catch (error) {
      console.error("Error creating LOP batch:", error);
      throw error;
    }
  }

  /**
   * Create a new appeal for a claim
   * @param {string} claimId - Claim ID
   * @param {Object} appealData - Appeal data
   * @returns {Promise<Object>} Created appeal
   */
  async createAppeal(claimId, appealData) {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/${claimId}/appeals`, appealData);
      return response;
    } catch (error) {
      console.error("Error creating appeal. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Get all appeals for a claim
   * @param {string} claimId - Claim ID
   * @returns {Promise<Array>} Appeals array
   */
  async getAppeals(claimId) {
    try {
      const response = await apiClient.get(`${this.BASE_URL}/${claimId}/appeals`);
      return response;
    } catch (error) {
      console.error("Error fetching appeals. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Get a specific appeal by ID
   * @param {string} claimId - Claim ID
   * @param {string} appealId - Appeal ID
   * @returns {Promise<Object>} Appeal data
   */
  async getAppealById(claimId, appealId) {
    try {
      const response = await apiClient.get(`${this.BASE_URL}/${claimId}/appeals/${appealId}`);
      return response;
    } catch (error) {
      console.error("Error fetching appeal by ID. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Update an appeal
   * @param {string} claimId - Claim ID
   * @param {string} appealId - Appeal ID
   * @param {Object} updates - Appeal updates
   * @returns {Promise<Object>} Updated appeal
   */
  async updateAppeal(claimId, appealId, updates) {
    try {
      const response = await apiClient.patch(`${this.BASE_URL}/${claimId}/appeals/${appealId}`, updates);
      return response;
    } catch (error) {
      console.error("Error updating appeal. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Submit an appeal
   * @param {string} claimId - Claim ID
   * @param {string} appealId - Appeal ID
   * @returns {Promise<Object>} Submitted appeal
   */
  async submitAppeal(claimId, appealId) {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/${claimId}/appeals/${appealId}/submit`);
      return response;
    } catch (error) {
      console.error("Error submitting appeal. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Resolve an appeal
   * @param {string} claimId - Claim ID
   * @param {string} appealId - Appeal ID
   * @param {Object} resolution - Resolution data
   * @param {Object} paymentInfo - Optional payment information
   * @param {string[]} medicationIds - Optional medication ids applied to payment
   * @param {Object} options - Optional options (e.g. { prorate: true })
   * @returns {Promise<Object>} Resolved appeal
   */
  async resolveAppeal(claimId, appealId, resolution, paymentInfo, medicationIds, options) {
    try {
      const body = {
        resolution,
        ...(paymentInfo && { paymentInfo }),
        ...(medicationIds && { medicationIds }),
        ...(options && { options })
      };
      const response = await apiClient.post(`${this.BASE_URL}/${claimId}/appeals/${appealId}/resolve`, body);
      return response;
    } catch (error) {
      console.error("Error resolving appeal. See network tab for details or server logs.");
      throw error;
    }
  }




  /**
   * Create multiple appeals in bulk
   * @param {Object} payload - { appeals: Array<{ claimId, medicationId, appealData }> }
   * @returns {Promise<Object>} Result with created and failed arrays
   */
  async createBulkAppeals(payload) {
    try {
      const response = await apiClient.post('/appeals/bulk/create', payload);
      return response;
    } catch (error) {
      console.error("Error creating bulk appeals. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Submit multiple appeals in bulk
   * @param {Object} payload - { submissions: Array<{ claimId, appealId }> }
   * @returns {Promise<Object>} Result with submitted and failed arrays
   */
  async submitBulkAppeals(payload) {
    try {
      const response = await apiClient.post('/appeals/bulk/submit', payload);
      return response;
    } catch (error) {
      console.error("Error submitting bulk appeals. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Resolve multiple appeals in bulk
   * @param {Object} payload - { resolutions: Array<{ claimId, appealId, resolution, paymentInfo, medicationIds, options }> }
   * @returns {Promise<Object>} Result with resolved and failed arrays
   */
  async resolveBulkAppeals(payload) {
    try {
      const response = await apiClient.post('/appeals/bulk/resolve', payload);
      return response;
    } catch (error) {
      console.error("Error resolving bulk appeals. See network tab for details or server logs.");
      throw error;
    }
  }

  /**
   * Search appeals with filters
   * @param {Object} filters - Search filters (patientIds, status, appealType, dateFrom, dateTo, hasDocumentation)
   * @returns {Promise<Array>} Array of appeals with claim and patient info
   */
  async searchAppeals(filters) {
    try {
      const response = await apiClient.post('/appeals/search', filters);
      return response;
    } catch (error) {
      console.error("Error searching appeals. See network tab for details or server logs.");
      throw error;
    }
  }
  /**
   * Get claims for a specific insurance (drill-down)
   * @param {string} insuranceId 
   * @param {number} page 
   * @param {number} limit 
   */
  async getClaimsByInsurance(insuranceId, page = 1, limit = 20) {
    try {
      const response = await apiClient.get(`/analytics/claims/insurance/${insuranceId}?page=${page}&limit=${limit}`);
      return response;
    } catch (error) {
      console.error("Error fetching claims by insurance:", error);
      throw error;
    }
  }

  /**
   * Merge multiple claims into a destination claim
   * @param {string[]} sourceClaimIds - Array of source claim IDs
   * @param {string} destinationClaimId - ID of the destination claim
   * @returns {Promise<Object>} Merged claim data
   */
  async mergeClaims(sourceClaimIds, destinationClaimId) {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/merge`, {
        sourceClaimIds,
        destinationClaimId
      });
      return response;
    } catch (error) {
      console.error("Error merging claims:", error);
      throw error;
    }
  }

  /**
   * Split a claim into multiple claims by provider
   * @param {string} claimId - Claim ID to split
   * @returns {Promise<Array>} Array of resulting claims
   */
  async splitClaim(claimId) {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/${claimId}/split`);
      return response.data?.data || response.data || response;
    } catch (error) {
      console.error("Error splitting claim:", error);
      throw error;
    }
  }

  /**
   * Manually split specific RXs into a new claim with a chosen provider
   * @param {string} claimId - Source Claim ID
   * @param {Object} data - { selectedMedicationIds: string[], targetProviderId: string, targetProviderContactId: string | null }
   * @returns {Promise<Object>} Object containing sourceClaim and newClaim
   */
  async manualSplitClaim(claimId, data) {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/${claimId}/manual-split`, data);
      return response.data?.data || response.data || response;
    } catch (error) {
      console.error("Error manually splitting claim:", error);
      throw error;
    }
  }


  /**
   * Get counts of claims grouped by status
   * @param {Object} params - Search params (filters) to respect when counting
   * @returns {Promise<Object>} Map of { [status]: count }
   */
  async getClaimStatusCounts(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      const supportedKeys = [
        'role',
        'patientId',
        'providerId',
        'caseNumber',
        'insuranceClaimId',
        'caseType',
        'createdBy',
        'hasAppeals',
        'hasPrescription',
        'isLitigated',
        'isRebill',
        'isOverallVerified',
        'hasProvider',
        'hasAppointment',
        'dateOfServiceStart',
        'dateOfServiceEnd',
        'createdAtStart',
        'createdAtEnd',
        'updatedAtStart',
        'updatedAtEnd',
      ];

      supportedKeys.forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          queryParams.append(key, params[key].toString());
        }
      });

      const response = await apiClient.get(`${this.BASE_URL}/status-counts?${queryParams.toString()}`);
      return response.data || response;
    } catch (error) {
      console.error("Error fetching claim status counts:", error);
      throw error;
    }
  }

  /**
   * Dev utility to create/delete sample claims
   * @param {string} action - 'create' or 'delete'
   * @returns {Promise<Object>}
   */
  async devSampleClaims(action) {
    return apiClient.post('/dev/sample', { action });
  }

  /**
   * Dev utility to create/delete reconciliation sample claims
   * @param {string} action - 'create' or 'delete'
   * @returns {Promise<Object>}
   */
  async devReconciliationSampleClaims(action) {
    return apiClient.post('/dev/reconciliation-sample', { action });
  }

  /**
   * Generate an LLM summary for an entity
   * @param {string} entityType - 'claim' | 'patient' | 'provider' | 'lawyer' | 'insurance'
   * @param {string} entityId - The ID of the entity
   * @param {string} timeRange - '7d' | '30d' | '90d' | 'all'
   * @returns {Promise<Object>} The summary object
   */
  async summarizeEntity(entityType, entityId, timeRange = '30d') {
    try {
      const response = await apiClient.post(`/llm/summarize/${entityType}/${entityId}`, { timeRange });
      return response.data || response;
    } catch (error) {
      console.error(`Error summarizing ${entityType}:`, error);
      throw error;
    }
  }

  async getClaimNoteIntelligence(claimId) {
    try {
      const response = await apiClient.get(`${this.BASE_URL}/${claimId}/note-intelligence`);
      return response.data?.data || response.data || response;
    } catch (error) {
      console.error('Error fetching claim note intelligence:', error);
      throw error;
    }
  }

  async recomputeClaimNoteIntelligence(claimId) {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/${claimId}/note-intelligence/recompute`);
      return response.data?.data || response.data || response;
    } catch (error) {
      console.error('Error recomputing claim note intelligence:', error);
      throw error;
    }
  }

  /**
   * Split a bulk EOB PDF into individual PDFs per claim and upload them.
   * @param {string} orgId 
   * @param {File} file 
   * @returns {Promise<Object>} Result with processedClaims and unaccountedPages
   */
  async splitAndProcessEob(orgId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('orgId', orgId);

      const response = await apiClient.post('/llm/eob/split-and-process', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data || response;
    } catch (error) {
      console.error("Error splitting and processing EOB:", error);
      throw error;
    }
  }

  /**
   * Get RxEvent statistics for the last completed day
   * @returns {Promise<Object>} { success, failure, failedIds }
   */
  async getRxEventStats() {
    try {
      const response = await apiClient.get('/dev/rx-event-stats');
      return response.data || response;
    } catch (error) {
      console.error("Error fetching RxEvent stats:", error);
      throw error;
    }
  }

  /**
   * Get the LLM feature-to-model registry
   * @returns {Promise<Object>} The registry mapping
   */
  async getLlmRegistry() {
    try {
      const response = await apiClient.get('/dev/llm-registry');
      return response.data || response;
    } catch (error) {
      console.error("Error fetching LLM registry:", error);
      throw error;
    }
  }

  /**
   * Update a mapping in the LLM registry
   * @param {string} feature - Feature name
   * @param {string} model - Model name
   * @returns {Promise<Object>} Result of the update
   */
  async updateLlmRegistry(feature, model) {
    try {
      const response = await apiClient.post('/dev/llm-registry', { feature, model });
      return response.data || response;
    } catch (error) {
      console.error("Error updating LLM registry:", error);
      throw error;
    }
  }

  /**
   * Bootstrap the LLM registry with default values
   * @returns {Promise<Object>} Result of the bootstrap
   */
  async bootstrapLlmRegistry() {
    try {
      const response = await apiClient.post('/dev/llm-registry/bootstrap');
      return response.data || response;
    } catch (error) {
      console.error("Error bootstrapping LLM registry:", error);
      throw error;
    }
  }

  /**
   * Update auto-copy fields configuration for a claim
   * @param {string} claimId - Claim ID
   * @param {string[]} autoCopyFields - Array of field names to auto-copy
   * @returns {Promise<Object>} Updated auto-copy fields
   */
  async updateAutoCopyFields(claimId, autoCopyFields) {
    try {
      const response = await apiClient.patch(`/claims/${claimId}/auto-copy-fields`, { autoCopyFields });
      return response.data || response;
    } catch (error) {
      console.error("Error updating auto-copy fields:", error);
      throw error;
    }
  }

  /**
   * Get auto-copy fields configuration for a claim
   * @param {string} claimId - Claim ID
   * @returns {Promise<Object>} Auto-copy fields configuration
   */
  async getAutoCopyFields(claimId) {
    try {
      const response = await apiClient.get(`/claims/${claimId}/auto-copy-fields`);
      return response.data || response;
    } catch (error) {
      console.error("Error getting auto-copy fields:", error);
      throw error;
    }
  }



  /**
   * Run full Pioneer Bulk ETL for all uploaded data
   * @param {Object} data - { attorneys, insurances, providers, patients, claims, medications }
   * @returns {Promise<Object>} Results of the Bulk ETL operation
   */
  async runPioneerBulkETL(data) {
    try {
      const response = await apiClient.post('/dev/run-pioneer-etl/bulk', data);
      return response.data || response;
    } catch (error) {
      console.error("Error running Pioneer Bulk ETL:", error);
      throw error;
    }
  }

  /**
   * Get Pioneer ingestion JSON data for a claim
   * @param {string} claimId - Claim ID
   * @returns {Promise<Object>} Pioneer data with full JSON for each RX
   */
  async getPioneerData(claimId) {
    try {
      const response = await apiClient.get(`/claims/${claimId}/pioneer-data`);
      return response.data || response;
    } catch (error) {
      console.error("Error getting Pioneer data:", error);
      throw error;
    }
  }
}

// Create a singleton instance
export const claimService = new ClaimService();
